import { Icon } from "@core/icons";
import { tokens } from "@core/theme/tokens";
import { ImageWithPlaceholder } from "@core/media";

export interface PromotionPreviewData {
  readonly eyebrow: string;
  readonly title: string;
  readonly subtitle: string;
  readonly cta: string;
  readonly imageUrl: string;
  /** Fecha ya formateada como el prototipo la muestra ("18 jul"), o `null`
   * cuando la promoción es de vigencia indefinida. */
  readonly until: string | null;
}

// PA-019-C (human-resolution-pa-019-c.json): SPEC-206 §7/criterio 6 exige que
// la promoción se vea "tal como se verá en la app" (RF-PIN-05). `AdminInicio`
// no tiene previsualización — el oráculo visual es la sección "Promociones"
// de la pantalla de inicio del miembro (`star-circle-app.jsx:1628-1653`,
// `Home`, carrusel de `SC.homeBanners`), reproducida acá para la única
// promoción en edición (no el carrusel completo: el formulario edita una a
// la vez). Mismo criterio de alcance que `WelcomePreview.tsx` (SPEC-205):
// reproduce únicamente lo visible de esa sección, no la pantalla `Home`
// entera (balance de puntos, autos, asistencia), que no es lo que este
// formulario administra.
export function PromotionPreview({
  eyebrow,
  title,
  subtitle,
  cta,
  imageUrl,
  until,
}: PromotionPreviewData) {
  return (
    <div
      // Mismo criterio que `WelcomePreview.tsx` tras la corrección de scroll
      // de SPEC-205 (spec-notes/SPEC-205.md §6): `minHeight`, nunca `height`
      // fijo con `overflow: hidden` — el marco `.scr-scroll` que envuelve
      // este lienzo (`HomeContentScreen.tsx`) es el único que recorta y
      // desplaza.
      style={{
        position: "relative",
        width: 402,
        minHeight: 420,
        background: tokens.surface.bg,
        padding: "22px 20px 26px",
        boxSizing: "border-box",
      }}
    >
      <div
        className="label"
        style={{ marginBottom: 12, color: tokens.text.tertiary }}
      >
        Promociones
      </div>
      <div
        style={{
          width: "100%",
          textAlign: "left",
          borderRadius: tokens.radius.lg,
          overflow: "hidden",
          border: `1px solid ${tokens.gold.line}`,
          background: tokens.surface.surface,
        }}
      >
        <div style={{ position: "relative", height: 150 }}>
          <ImageWithPlaceholder
            src={imageUrl}
            iconSize={24}
            imageStyle={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(10,10,11,0.15) 0%, rgba(10,10,11,0.9) 100%)",
            }}
          />
          {eyebrow ? (
            <span
              style={{
                position: "absolute",
                top: 14,
                left: 15,
                font: `700 9.5px ${tokens.font.ui}`,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "#16201B",
                background: `linear-gradient(135deg,${tokens.gold.light},${tokens.gold.deep})`,
                padding: "6px 11px",
                borderRadius: 999,
              }}
            >
              {eyebrow}
            </span>
          ) : null}
        </div>
        <div style={{ padding: "16px 18px 18px" }}>
          <div
            className="display"
            style={{
              fontSize: 22,
              lineHeight: 1.15,
              color: tokens.text.primary,
            }}
          >
            {title || "Promoción sin título"}
          </div>
          {subtitle ? (
            <p
              style={{
                font: `400 12.5px/1.5 ${tokens.font.ui}`,
                color: tokens.text.secondary,
                margin: "7px 0 14px",
              }}
            >
              {subtitle}
            </p>
          ) : null}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: subtitle ? 0 : 14,
            }}
          >
            {until ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  font: `500 11px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                }}
              >
                <Icon name="clock" size={13} /> Hasta {until}
              </span>
            ) : (
              <span
                style={{
                  font: `500 11px ${tokens.font.ui}`,
                  color: tokens.text.tertiary,
                }}
              >
                Sin fecha límite
              </span>
            )}
            {cta ? (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  font: `600 12.5px ${tokens.font.ui}`,
                  color: tokens.gold.light,
                }}
              >
                {cta} <Icon name="arrow" size={15} stroke={1.8} />
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
